import { useEffect, useState, useCallback, useRef } from 'react';
import { X, Loader2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import api from '../api';
import {
  UploadDropzone,
  MediaThumb,
  MediaToolbar,
} from '../pages/MediaLibrary';

/**
 * Modal media picker. Used by Page / Event / News editors.
 *
 * Props:
 *  - open: boolean
 *  - onClose: () => void
 *  - onSelect: (item) => void  // item is the media record
 *  - accept: 'image' (default) | 'any'
 *  - title: optional title override
 */
export default function MediaPicker({ open, onClose, onSelect, accept = 'image', title }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [kind, setKind] = useState(accept === 'image' ? 'image' : '');
  const [sort, setSort] = useState('newest');
  const [selectedId, setSelectedId] = useState(null);
  const [stats, setStats] = useState({ by_kind: {} });
  const debounceRef = useRef(null);

  const load = useCallback(async (params = {}) => {
    setLoading(true);
    try {
      const { data } = await api.get('/media', { params });
      setItems(data);
    } catch (err) {
      toast.error('Failed to load media');
    } finally {
      setLoading(false);
    }
  }, []);

  // initial + refresh when filters change
  useEffect(() => {
    if (!open) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      load({ q: q || undefined, kind: kind || undefined, sort });
    }, q ? 300 : 0);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
     
  }, [open, q, kind, sort]);

  // stats for filter counts
  useEffect(() => {
    if (!open) return;
    api.get('/media/stats').then((r) => setStats(r.data)).catch(() => {});
    setSelectedId(null);
     
  }, [open]);

  const onUploaded = (item) => {
    setItems((arr) => [item, ...arr.filter((x) => x.id !== item.id)]);
    setSelectedId(item.id);
  };

  const handleConfirm = () => {
    const item = items.find((i) => i.id === selectedId);
    if (!item) return;
    onSelect?.(item);
    onClose?.();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-8" data-testid="media-picker">
      <div className="absolute inset-0 bg-black/65 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-6xl max-h-full bg-[#16131a] border border-white/[0.08] rounded-2xl text-white flex flex-col overflow-hidden shadow-[0_30px_60px_-15px_rgba(0,0,0,0.7)]">
        {/* Header */}
        <div className="px-6 py-4 border-b border-white/[0.06] flex items-center justify-between shrink-0">
          <div>
            <div className="text-[11px] uppercase tracking-[0.18em] text-[#5DCAB5] font-semibold">Media Library</div>
            <div className="font-serif-display text-[20px] font-bold mt-0.5">{title || 'Choose a file'}</div>
          </div>
          <button onClick={onClose} className="h-9 w-9 rounded-lg bg-white/5 hover:bg-white/10 flex items-center justify-center" data-testid="media-picker-close">
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          <UploadDropzone onUploaded={onUploaded} compact />

          <MediaToolbar
            q={q} setQ={setQ}
            kind={kind} setKind={setKind}
            sort={sort} setSort={setSort}
            kindCounts={stats.by_kind || {}}
          />

          {loading ? (
            <div className="flex items-center justify-center py-16 text-white/45">
              <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
            </div>
          ) : items.length === 0 ? (
            <div className="rounded-xl bg-[#0f0d12] border border-dashed border-white/[0.1] py-12 text-center text-white/55 text-[13px]">
              No matching media. Upload something using the zone above.
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4" data-testid="media-picker-grid">
              {items.map((it) => (
                <MediaThumb
                  key={it.id}
                  item={it}
                  selected={selectedId === it.id}
                  onClick={() => setSelectedId(it.id)}
                />
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-white/[0.06] flex items-center justify-between shrink-0 bg-[#16131a]">
          <div className="text-[12px] text-white/45">
            {selectedId ? (
              <span><span className="text-white font-semibold">1</span> selected — confirm to use this file.</span>
            ) : (
              <span>Click a file to select it, then confirm.</span>
            )}
          </div>
          <div className="flex gap-2">
            <Button onClick={onClose} variant="ghost" className="h-10 rounded-lg text-white/70 hover:bg-white/10 hover:text-white font-semibold px-4">
              Cancel
            </Button>
            <Button
              onClick={handleConfirm}
              disabled={!selectedId}
              className="h-10 rounded-lg bg-[#E5614C] hover:bg-[#d04b36] disabled:opacity-50 text-white font-semibold px-5 btn-press"
              data-testid="media-picker-confirm"
            >
              Use this file
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
